import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Star, Package, Loader2, AlertCircle, Check, MessageSquare } from "lucide-react";
import { API_BASE_URL } from "../config/api";

interface ReviewData {
    claim: {
        id: number;
        name: string;
        status: string;
        category: string;
        subService: string;
        rating: number | null;
        review: string | null;
        alreadyReviewed: boolean;
    };
    product: {
        name: string;
        brand: string | null;
    } | null;
}

const ReviewPage: React.FC = () => {
    const { claimId } = useParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [data, setData] = useState<ReviewData | null>(null);
    const [rating, setRating] = useState(0);
    const [hoveredStar, setHoveredStar] = useState(0);
    const [comment, setComment] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        if (!claimId) {
            setError("No Service Request ID provided");
            setLoading(false);
            return;
        }

        const fetchData = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/api/claim/review/${claimId}`);
                if (!res.ok) {
                    const err = await res.json().catch(() => ({}));
                    throw new Error(err.message || "Service request not found");
                }
                const result = await res.json();
                setData(result);

                if (result.claim.alreadyReviewed) {
                    setRating(result.claim.rating || 0);
                    setComment(result.claim.review || "");
                    setSubmitted(true);
                }
            } catch (err: any) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [claimId]);

    const handleSubmit = async () => {
        if (rating === 0) return;

        setSubmitting(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/claim/review`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    id: Number(claimId),
                    rating,
                    comment: comment.trim() || null,
                }),
            });

            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.message || "Failed to submit review");
            }

            setSubmitted(true);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setSubmitting(false);
        }
    };

    const getRatingLabel = (r: number): string => {
        switch (r) {
            case 1:
                return "Poor";
            case 2:
                return "Fair";
            case 3:
                return "Good";
            case 4:
                return "Very Good";
            case 5:
                return "Excellent";
            default:
                return "";
        }
    };

    // Loading
    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-[#0c1222] via-[#0f172a] to-[#020617] flex items-center justify-center">
                <div className="flex flex-col items-center gap-4">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
                    <p className="text-sm text-gray-400">Loading...</p>
                </div>
            </div>
        );
    }

    // Error
    if (error && !data) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-[#0c1222] via-[#0f172a] to-[#020617] flex items-center justify-center px-4">
                <div className="bg-[#111827]/80 backdrop-blur-xl border border-[#1e293b] rounded-3xl p-8 max-w-sm w-full text-center">
                    <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
                    <p className="text-lg text-white font-semibold mb-2">Something Went Wrong</p>
                    <p className="text-sm text-gray-400">{error}</p>
                </div>
            </div>
        );
    }

    // Success / Already Reviewed
    if (submitted) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-[#0c1222] via-[#0f172a] to-[#020617] flex items-center justify-center px-4">
                <div className="bg-[#111827]/80 backdrop-blur-xl border border-[#1e293b] rounded-3xl p-8 max-w-md w-full text-center">
                    <div className="w-16 h-16 bg-green-500/10 rounded-2xl flex items-center justify-center mx-auto mb-5 border border-green-500/20">
                        <Check className="w-8 h-8 text-green-400" />
                    </div>
                    <p className="text-xl text-white font-bold mb-2">Thank You!</p>
                    <p className="text-sm text-gray-400 mb-6">
                        {data?.claim.alreadyReviewed
                            ? "You've already submitted your review."
                            : "Your review has been submitted successfully."}
                    </p>

                    {/* Show submitted rating */}
                    <div className="flex items-center justify-center gap-1 mb-4">
                        {[1, 2, 3, 4, 5].map((star) => (
                            <Star
                                key={star}
                                className={`w-7 h-7 ${star <= rating ? "text-yellow-400 fill-yellow-400" : "text-gray-600"}`}
                            />
                        ))}
                    </div>
                    <p className="text-sm text-yellow-400 font-semibold mb-4">{getRatingLabel(rating)}</p>

                    {comment && (
                        <div className="bg-[#1e293b]/50 rounded-xl p-4 text-left">
                            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1 font-semibold">
                                Your Review
                            </p>
                            <p className="text-sm text-gray-300">{comment}</p>
                        </div>
                    )}

                    {data?.product && (
                        <div className="mt-6 pt-4 border-t border-[#1e293b]">
                            <p className="text-xs text-gray-500">Service for</p>
                            <p className="text-sm text-white font-medium mt-1">{data.product.name}</p>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // Review Form
    return (
        <div className="min-h-screen bg-gradient-to-br from-[#0c1222] via-[#0f172a] to-[#020617]">
            {/* Header */}
            <div className="bg-[#0f172a]/80 backdrop-blur-xl border-b border-[#1e293b]/50 sticky top-0 z-10">
                <div className="max-w-lg mx-auto px-4 py-4 flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-700 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/20">
                        <Package className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <span className="text-sm font-bold text-white tracking-wider">CARE+ SERVICE</span>
                        <p className="text-xs text-gray-500">by Secured Stitch</p>
                    </div>
                </div>
            </div>

            <div className="max-w-lg mx-auto px-4 py-8 space-y-6">
                {/* Title */}
                <div className="text-center">
                    <div className="w-16 h-16 bg-yellow-500/10 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-yellow-500/20">
                        <Star className="w-8 h-8 text-yellow-400" />
                    </div>
                    <h1 className="text-2xl font-bold text-white mb-2">Rate Your Experience</h1>
                    <p className="text-sm text-gray-400">
                        How was your Care+ service experience? Your feedback helps us improve.
                    </p>
                </div>

                {/* Product Info */}
                {data?.product && (
                    <div className="bg-[#111827]/70 backdrop-blur-sm border border-[#1e293b]/70 rounded-2xl p-4">
                        <div className="flex items-center gap-3">
                            <Package className="w-5 h-5 text-blue-400 flex-shrink-0" />
                            <div>
                                <p className="text-sm text-white font-semibold">{data.product.name}</p>
                                <p className="text-xs text-gray-500">
                                    {data.product.brand || "—"} • Service Request : {claimId}
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Star Rating */}
                <div className="bg-[#111827]/70 backdrop-blur-sm border border-[#1e293b]/70 rounded-2xl p-6 text-center">
                    <p className="text-sm text-gray-400 mb-4 font-medium">Tap to rate</p>
                    <div className="flex items-center justify-center gap-2 mb-3">
                        {[1, 2, 3, 4, 5].map((star) => (
                            <button
                                key={star}
                                onClick={() => setRating(star)}
                                onMouseEnter={() => setHoveredStar(star)}
                                onMouseLeave={() => setHoveredStar(0)}
                                className="transition-transform hover:scale-125 active:scale-95"
                            >
                                <Star
                                    className={`w-10 h-10 transition-colors ${
                                        star <= (hoveredStar || rating)
                                            ? "text-yellow-400 fill-yellow-400"
                                            : "text-gray-600 hover:text-gray-500"
                                    }`}
                                />
                            </button>
                        ))}
                    </div>
                    {(hoveredStar || rating) > 0 && (
                        <p className="text-sm text-yellow-400 font-semibold animate-fade-in">
                            {getRatingLabel(hoveredStar || rating)}
                        </p>
                    )}
                </div>

                {/* Comment */}
                <div className="bg-[#111827]/70 backdrop-blur-sm border border-[#1e293b]/70 rounded-2xl p-5">
                    <div className="flex items-center gap-2 mb-3">
                        <MessageSquare className="w-4 h-4 text-gray-500" />
                        <p className="text-xs text-gray-500 uppercase tracking-widest font-bold">
                            Your Review{" "}
                            <span className="text-gray-600 normal-case tracking-normal font-normal">(optional)</span>
                        </p>
                    </div>
                    <textarea
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        rows={4}
                        maxLength={500}
                        placeholder="Tell us about your experience..."
                        className="w-full px-4 py-3 bg-[#1e293b]/50 border border-[#2d3748] rounded-xl text-sm text-gray-200 placeholder-gray-600 focus:outline-none focus:border-blue-500 transition-colors resize-none"
                    />
                    <p className="text-xs text-gray-600 mt-2 text-right">{comment.length}/500</p>
                </div>

                {/* Error */}
                {error && (
                    <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-xl flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                        <p className="text-sm text-red-400">{error}</p>
                    </div>
                )}

                {/* Submit Button */}
                <button
                    onClick={handleSubmit}
                    disabled={rating === 0 || submitting}
                    className={`w-full py-3.5 rounded-xl text-sm font-semibold transition-all ${
                        rating === 0
                            ? "bg-[#1e293b] text-gray-600 cursor-not-allowed"
                            : submitting
                              ? "bg-blue-600/50 text-blue-300 cursor-wait"
                              : "bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-500/20"
                    }`}
                >
                    {submitting ? (
                        <span className="flex items-center justify-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin" /> Submitting...
                        </span>
                    ) : rating === 0 ? (
                        "Select a rating to continue"
                    ) : (
                        `Submit ${getRatingLabel(rating)} Review`
                    )}
                </button>

                {/* Footer */}
                <div className="text-center pt-4 pb-8">
                    <p className="text-xs text-gray-700">
                        © {new Date().getFullYear()} Secured Stitch. All rights reserved.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default ReviewPage;
